import { Component, OnInit } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { ProjectProcessItem, ProjectProcessService } from '@app/services/project-process.service';
import { TypeaheadService, CustomerItem } from '@app/services/typeahead.service';
import { TravelBillDietInfo, TravelBillGet, TravelBillLabour, TravelBillProject, TravelBillResource, TravelBillService } from '@app/services/travel-bill.service';
import { StateService } from '@app/core/services/state.service';

@Component({
    selector: 'tt-travel-bill',
    templateUrl: './travel-bill.component.html',
    styleUrls: ['./travel-bill.component.css'],
    standalone: false
})
export class TravelBillComponent implements OnInit {
    private readonly params: { [key: string]: string };
    
    public page?: TravelBillGet;

    public selectListLabours: TravelBillLabour[] = [];

    public selectListProjects: TravelBillProject[] = [];

    public selectListProjectProcesses: ProjectProcessItem[] = [];

    public selectListSrResourcesFrom: TravelBillResource[] = [];

    public saving = false;

    constructor(
        private stateService: StateService,
        private modal: ModalService,
        private travelBill: TravelBillService,
        private economy: ProjectProcessService,
        private typeahead: TypeaheadService
    ) {
        this.params = this.stateService.getStateParams();
    }

    public async typeaheadSearch(params?: { [key: string]: any }): Promise<CustomerItem[]> {
        const value = params?.['value'] ?? '';

        if (this.page) this.page.cust_name = value;

        return this.typeahead.searchCustomer({
            cust_name: value || '',
            custtype: 'C',
            lastselect: 'SEARCH'
        });
    }

    public onTypeaheadSelected(item?: CustomerItem | null): void {
        if (!this.page) return;

        if (item) {
            this.page.cust_no = item.item_id ?? '0';
            this.page.cust_name = item.item_name ?? '';
            void this.loadProjects();
        } else {
            this.onTypeaheadClear();
        }
    }

    public onTypeaheadChanged(value: string, item?: CustomerItem | null): void {
        if (!this.page) return;

        this.page.cust_name = value;

        if (!value) {
            this.onTypeaheadClear();
        } else if (item?.item_id) {
            this.page.cust_no = item.item_id;
            void this.loadProjects();
        }
    }

    public onTypeaheadClear(): void {
        if (!this.page) return;

        this.page.cust_no = '0';
        this.page.cust_name = '';
        this.page.project_keyno = '0';
        this.selectListProjects = [];
        this.selectListProjectProcesses = [];
    }

    public onProjectChanged(value: string): void {
        if (!this.page || value === this.page.project_keyno) return;

        this.page.project_keyno = value;

        if (this.hasParameterValue(value)) void this.loadProjectProcesses();
    }

    public onDateChanged(value: string, field: 'start_dt' | 'end_dt'): void {
        if (!this.page || value === this.page[field]) return;

        this.page[field] = value;

        if (this.page.start_dt && this.page.end_dt) void this.retrieveDietDaysInfo();
    }

    public async saveChanges() {
	    if (!this.page || this.saving) return;

        this.saving = true;
	    
	    try {
		    const { errorcode, errormessage } = await this.travelBill.save(this.page);
		
		    if (errorcode !== '0') {
			    this.modal.show({ 
				    type: 'warning', 
				    title: 'error', 
				    translateTitle: true, 
				    message: errormessage 
			    });
		    } else {
			    this.stateService.back();
		    }
	    } finally {
		    this.saving = false;
	    }
    }
	
    private async loadEdit(): Promise<void> {
        const result = await this.travelBill.getEdit({
            argtype: this.params['argtype'],
            argvalue: this.params['argvalue'],
            travelbill_no: this.params['travelbill_no'],
        });

        this.page = result;
    }

    private async loadLabours(): Promise<void> {
        this.selectListLabours = await this.travelBill.listLabours();
    }

    private async loadProjects(): Promise<void> {
        if (!this.page || !this.hasParameterValue(this.page.cust_no)) return;

        this.selectListProjects = await this.travelBill.listProjects({ cust_no: this.page.cust_no });
    }

    private async loadProjectProcesses(): Promise<void> {
        if (!this.page || !this.hasParameterValue(this.page.project_keyno)) return;

        this.selectListProjectProcesses = await this.economy.getProjectProcesses({
            project_keyno: this.page.project_keyno,
            show_only_timeentry_allowed: '1'
        });
    }

    private async loadSrResourcesFrom(): Promise<void> {
        if (!this.page || !this.hasParameterValue(this.page.custact_no)) return;

        this.selectListSrResourcesFrom = await this.travelBill.loadSpecialResourcesFrom(this.page.custact_no);
    }

    private hasParameterValue(value: string | undefined): boolean {
        return value !== undefined && value !== null && value.trim().length > 0;
    }

    private async retrieveDietDaysInfo(): Promise<void> {
        if (!this.page?.start_dt || !this.page.end_dt) return;

        const info: TravelBillDietInfo = await this.travelBill.updateDietDaysInfo({
            start_dt: this.page.start_dt,
            end_dt: this.page.end_dt
        });

        Object.assign(this.page, info);
    }
    
    public async ngOnInit(): Promise<void> {
        await this.loadEdit();

        if (!this.page) return;
        if (this.page.sr === '0') void this.loadLabours();

        void this.loadProjects();
        void this.loadProjectProcesses();
        void this.loadSrResourcesFrom();
    }
}
